import {
    type KeyboardEventHandler,
    type ChangeEvent,
    type ReactNode,
    type Ref,
    forwardRef,
    useState,
    useEffect,
    useRef,
} from 'react';
import classnames from 'classnames';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';

import styles from './phoneInput.less';

interface CallingCodeInputProps {
    flag: ReactNode;
    onChange: (value: string) => void;
    value: string;
    onFocus: VoidFunction;
    onBlur: VoidFunction;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    invalid?: boolean;
    disabled?: boolean;
    className?: string | boolean;
    inputElementRef: Ref<HTMLInputElement>;
}

export const CallingCodeInput = forwardRef<HTMLDivElement, CallingCodeInputProps>(
    ({ onFocus, onBlur, value, onChange, flag, onKeyDown, invalid, disabled, className, inputElementRef }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const inputRefMultiplexer = useMultipleRefs(inputRef, inputElementRef);
        const [isFocused, setIsFocused] = useState(false);
        const [isFocusVisible, setIsFocusVisible] = useState(false);
        const { isMobile } = useBreakpoint();

        useEffect(() => {
            if (inputRef.current) {
                setIsFocusVisible(isFocused && inputRef.current.classList.contains('focus-visible'));
            }
        }, [isFocused]);

        return (
            <div
                className={classnames(styles.callingCodeInputContainer, className, {
                    [styles.focusVisible]: isFocusVisible && !disabled,
                    [styles.focused]: isFocused && !disabled,
                    [styles.invalid]: invalid && !disabled,
                    [styles.disabled]: disabled,
                })}
                tabIndex={disabled ? -1 : 0}
                onFocus={() => {
                    if (disabled) {
                        return;
                    }
                    if (!isMobile) {
                        inputRef.current?.focus();
                        return;
                    }

                    setIsFocused(true);
                    onFocus();
                }}
                onBlur={() => {
                    if (!isMobile) {
                        return;
                    }
                    setIsFocused(false);
                    onBlur();
                }}
                ref={ref}
                data-qa="magritte-phone-input-calling-code"
            >
                <div className={styles.flagContainer}>{flag}</div>
                <div className={styles.inputContainer}>
                    <input
                        ref={inputRefMultiplexer}
                        onFocus={() => {
                            setIsFocused(true);
                            onFocus();
                        }}
                        onBlur={() => {
                            setIsFocused(false);
                            onBlur();
                        }}
                        onKeyDown={onKeyDown}
                        className={styles.callingCodeInput}
                        value={value}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                        disabled={disabled}
                        data-qa="magritte-phone-input-calling-code-input"
                    />
                    <div className={styles.ghostData} data-qa="magritte-phone-input-calling-code-value">
                        {value}
                    </div>
                </div>
            </div>
        );
    }
);

CallingCodeInput.displayName = 'CallingCodeInput';
